import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchFinanceUsersReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { FinanceUsersReportApiItem } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { useReportCsvExport } from './useReportCsvExport';

function financeUsersRowId(row: FinanceUsersReportApiItem) {
  return String(row.user_id);
}

/** Finance's weekly users export: one row per user. The CSV keeps Finance's own column headers. */
export function FinanceUsersReportPage() {
  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'user_name', headerName: 'Name', minWidth: 200, flex: 0.3 },
    { field: 'user_name_arabic', headerName: 'Arabic name', minWidth: 200, flex: 0.3, sortable: false },
    { field: 'nid_iqama', headerName: 'NID / Iqama', width: 130, sortable: false },
    { field: 'user_email', headerName: 'Email', minWidth: 200, flex: 0.3, sortable: false },
    { field: 'user_phone', headerName: 'Phone', width: 150, sortable: false },
    { field: 'age', headerName: 'Age', type: 'number', width: 80, sortable: false },
    { field: 'gender', headerName: 'Gender', width: 100, sortable: false },
    { field: 'nationality', headerName: 'Nationality', width: 140, sortable: false },
    { field: 'status', headerName: 'Status', width: 170 },
    { field: 'register_date', headerName: 'Register date', width: 200 },
    { field: 'last_activity_date', headerName: 'Last activity', width: 200, sortable: false },
    {
      field: 'net_subscriptions_amount',
      headerName: 'Net subscriptions',
      width: 160,
      sortable: false,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'net_redemptions_amount',
      headerName: 'Net redemptions',
      width: 160,
      sortable: false,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'market_portfolio_size',
      headerName: 'Portfolio size',
      width: 150,
      sortable: false,
      valueFormatter: gridFormatDecimalString,
    },
    { field: 'number_of_subscription_orders', headerName: 'Subscription orders', width: 160, sortable: false },
    { field: 'number_of_redemption_orders', headerName: 'Redemption orders', width: 160, sortable: false },
    { field: 'survey_score', headerName: 'Survey score', width: 120, sortable: false },
    { field: 'risk_profile', headerName: 'Risk profile', width: 140, sortable: false },
    { field: 'first_investment_date', headerName: 'First investment', width: 200 },
    { field: 'last_investment_date', headerName: 'Last investment', width: 200 },
    { field: 'is_premium', headerName: 'Premium', width: 100 },
    { field: 'subscription_start_date', headerName: 'Premium start', width: 200, sortable: false },
    { field: 'subscription_end_date', headerName: 'Premium end', width: 200, sortable: false },
    { field: 'customer_company', headerName: 'Company', minWidth: 160, flex: 0.2, sortable: false },
    { field: 'address_or_postal_code', headerName: 'City, postal code', minWidth: 160, flex: 0.2, sortable: false },
    { field: 'address', headerName: 'Address', minWidth: 240, flex: 0.4, sortable: false },
  ]);

  const { financeUsersReportItems, totalFinanceUsersReportItems, fetchFinanceUsersReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'finance-users-report.csv',
    useCallback(() => reportService.exportFinanceUsersReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Finance users</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchFinanceUsersReport}
          rows={financeUsersReportItems}
          columns={columns.current}
          getRowId={financeUsersRowId}
          loading={fetchFinanceUsersReportStatus === fetchStatuses.pending}
          rowCount={totalFinanceUsersReportItems}
          initialSortModel={[{ field: 'user_id', sort: 'asc' }]}
        />
      </Grid>
    </>
  );
}
